/* AgentMeeting — Room variation B: Round-table seating + center stage
 *
 * Wired to the live backend via `useMeetingStream` + `meetings.get/messages`.
 */
import React from 'react';
import { AGENTS, AgentT, STANCE_COLOR } from '../shared/data';
import {
  Avatar, StanceTag, ModelChip, RoundSteps, CostMeter, RoleSwitcher,
  renderInline, I, Role,
} from '../shared/ui';
import { meetings as meetingsApi, HTTPError, type ServerEvent } from '../shared/api';
import { useMeetingStream } from '../shared/useMeetingStream';
import { useAuth } from '../shared/auth';
import { useI18n } from '../shared/i18n';

type Props = { role: Role; meetingType: string; embedded?: boolean; topBar?: React.ReactNode };

interface ServerMsg {
  id: string;
  round_no: number;
  participant_id: string;
  kind: string;
  content: string;
  tokens_in?: number;
  tokens_out?: number;
  cost_cents?: number;
  parent_id?: string | null;
  created_at?: string;
  speaker_name?: string;
  speaker_short?: string;
  speaker_stance?: string;
  speaker_model?: string;
  _streaming?: boolean;
}

function synthAgent(m: ServerMsg): AgentT {
  const stance = (m.speaker_stance || 'neutral') as AgentT['stance'];
  return {
    id: m.participant_id || m.id,
    name: m.speaker_name || '—',
    short: m.speaker_short || '··',
    stance,
    stanceLabel: '',
    model: (m.speaker_model || null) as AgentT['model'],
    persona: '',
  };
}

function fmtTs(iso?: string): string {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-GB', { hour12: false }); }
  catch { return ''; }
}

function goMeetings() {
  if (typeof window !== 'undefined') location.hash = '#/app/meetings';
}

export function RoomRoundTable({ role, meetingType, embedded, topBar }: Props) {
  useAuth();
  const { t } = useI18n();
  const meetingId = typeof window !== 'undefined'
    ? localStorage.getItem('agentmeeting.currentMeetingId')
    : null;

  const [serverMessages, setServerMessages] = React.useState<ServerMsg[] | null>(null);
  const [meetingMeta, setMeetingMeta] = React.useState<any>(null);
  const [loadErr, setLoadErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!meetingId) return;
    meetingsApi.get(meetingId)
      .then(setMeetingMeta)
      .catch((e: unknown) => setLoadErr(e instanceof HTTPError ? `meeting ${e.status}` : `${e}`));
    meetingsApi.messages(meetingId)
      .then(r => setServerMessages((r.messages || []) as ServerMsg[]))
      .catch((e: unknown) => setLoadErr(e instanceof HTTPError ? `messages ${e.status}` : `${e}`));
  }, [meetingId]);

  const streamOpts = {
    onTurnToken: (ev: ServerEvent) => {
      const { message_id, delta } = ev.payload || {};
      if (!message_id) return;
      setServerMessages(prev => (prev || []).map(m =>
        m.id === message_id ? { ...m, content: (m.content || '') + (delta || '') } : m
      ));
    },
    onTurnEnd: (ev: ServerEvent) => {
      const p = ev.payload || {};
      setServerMessages(prev => (prev || []).map(m =>
        m.id === p.message_id ? {
          ...m,
          content: p.content ?? m.content,
          tokens_in: p.tokens_in ?? m.tokens_in,
          tokens_out: p.tokens_out ?? m.tokens_out,
          cost_cents: p.cost_cents ?? m.cost_cents,
          _streaming: false,
        } : m
      ));
    },
    onMeetingState: (_ev: ServerEvent) => { /* banner could be added later */ },
    onAgendaAdvance: (ev: ServerEvent) => {
      setMeetingMeta((prev: any) => ({ ...(prev || {}), current_round: ev.payload?.round_no }));
    },
  };
  const { events, connected } = useMeetingStream(meetingId, streamOpts as any);

  // Pick up turn.start from events array (hook doesn't expose it directly).
  const lastSeenIdx = React.useRef(0);
  React.useEffect(() => {
    for (let i = lastSeenIdx.current; i < events.length; i++) {
      const ev = events[i];
      if (ev.type === 'turn.start') {
        const p = ev.payload || {};
        if (!p.message_id) continue;
        setServerMessages(prev => {
          const list = prev || [];
          if (list.some(m => m.id === p.message_id)) return list;
          return [...list, {
            id: p.message_id,
            round_no: p.round_no ?? 0,
            participant_id: p.speaker_id || '',
            speaker_name: p.speaker_name,
            speaker_short: p.speaker_short,
            speaker_stance: p.speaker_stance,
            speaker_model: p.speaker_model,
            kind: 'speech',
            content: '',
            _streaming: true,
            created_at: new Date().toISOString(),
          }];
        });
      }
    }
    lastSeenIdx.current = events.length;
  }, [events]);

  // ── Empty state ────────────────────────────────────────────
  if (!meetingId) {
    return (
      <div className="frame room-frame col" style={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        {topBar}
        <div className="col gap-3" style={{
          maxWidth: 420, padding: 24, border: '1px solid var(--line-2)',
          borderRadius: 6, background: 'var(--bg-1)', textAlign: 'center',
        }}>
          <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>NO MEETING SELECTED</div>
          <div style={{ fontSize: 15 }}>Pick a meeting to enter the round-table.</div>
          <button className="btn primary" onClick={goMeetings} title={t('app.meetings.my')}>Open meetings →</button>
        </div>
      </div>
    );
  }

  if (serverMessages == null) {
    return (
      <div className="frame room-frame col" style={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        {topBar}
        <div className="mono tiny dim">loading transcript…</div>
        {loadErr && <div className="mono tiny" style={{ color: 'var(--err)', marginTop: 8 }}>{loadErr}</div>}
      </div>
    );
  }

  const liveMsg = serverMessages.find(m => m._streaming) || serverMessages[serverMessages.length - 1];
  const liveAgent: AgentT | null = liveMsg ? synthAgent(liveMsg) : null;
  const speakers = AGENTS.filter(a => a.id !== 'U1');

  const currentRound = meetingMeta?.current_round ?? 0;
  const totalRounds = meetingMeta?.total_rounds ?? Math.max(currentRound + 1, 6);

  const onPause = async () => {
    if (!meetingId) return;
    try { await meetingsApi.pause(meetingId); } catch (e) { setLoadErr(`${e}`); }
  };

  return (
    <div className="frame room-frame" style={{ height: '100%' }}>
      {topBar}
      {!embedded && (
        <div className="term-bar">
          <div className="lights"><span /><span /><span /></div>
          <div className="crumbs">
            <span>~/meetings/{meetingMeta?.title || meetingId.slice(0, 8)}</span>
            <span className="sep">/</span>
            <span className="here">round-table</span>
          </div>
          <div className="right">
            <CostMeter
              tokens={Math.round((meetingMeta?.spent_cents || 0) * 40)}
              cents={meetingMeta?.spent_cents || 0}
              budget={meetingMeta?.budget_cents || 500}
            />
            <RoleSwitcher role={role} onChange={() => {}} compact />
          </div>
        </div>
      )}

      <div style={{ padding: '14px 22px 12px', display: 'flex', alignItems: 'center', gap: 18, borderBottom: '1px solid var(--line-1)' }}>
        <div className="col gap-1 grow">
          <div className="meta">
            <span className="dim">[{(meetingMeta?.type || meetingType).toUpperCase()}]</span> {meetingMeta?.title || meetingId.slice(0, 12)} · {t('app.room.agenda')}
          </div>
          <div style={{ fontSize: 16, fontWeight: 500 }}>
            {meetingMeta?.topic || '(no topic)'}
          </div>
        </div>
        <RoundSteps current={currentRound} total={totalRounds} />
        <div className="flex gap-1 center">
          <span className={'dot ' + (connected ? 'live pulse' : '')} />
          <span className="mono tiny live-fg" style={{ letterSpacing: '0.06em' }}>
            {connected ? 'LIVE' : 'OFFLINE'}
          </span>
        </div>
      </div>

      <div className="grow flex room-body" style={{ minHeight: 0 }}>
        <div className="col grow" style={{ minWidth: 0, position: 'relative' }}>
          <Table speakers={speakers} liveAgent={liveAgent} currentRound={currentRound} />
          {liveMsg && liveAgent && (
            <div className="room-b-bubble" style={{
              position: 'absolute', left: 28, right: 28, bottom: 22,
              background: 'var(--bg-1)', border: '1px solid var(--line-2)',
              borderRadius: 6, padding: '12px 16px',
            }}>
              <CenterSpeech msg={liveMsg} agent={liveAgent} />
            </div>
          )}
        </div>

        <div className="vr room-side-vr" />
        <div className="col room-side" style={{ width: 320, flex: '0 0 320px', background: 'var(--bg-1)' }}>
          <div className="flex center between" style={{ padding: '12px 14px', borderBottom: '1px solid var(--line-1)' }}>
            <span className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>TRANSCRIPT · {serverMessages.length} turns</span>
            <button className="btn ghost icon" disabled title={t('app.meetings.search')}>{I.search}</button>
          </div>
          <div className="grow scroll col" style={{ padding: '4px 0' }}>
            {serverMessages.length === 0 && (
              <div className="meta dim" style={{ padding: 24, textAlign: 'center' }}>
                (no turns yet)
              </div>
            )}
            {serverMessages.map((m, i) => <TickerRow key={m.id} msg={m} idx={i + 1} />)}
          </div>

          <div className="hr" />
          <div className="col gap-2" style={{ padding: 14 }}>
            <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>STATUS</div>
            <div className="meta">
              {connected ? 'connected · streaming' : 'reconnecting…'}
              {loadErr && <> · <span style={{ color: 'var(--err)' }}>{loadErr}</span></>}
            </div>
            {role === 'director' && (
              <button className="btn" onClick={onPause}>{I.pause} {t('app.room.director.pause')}</button>
            )}
          </div>
        </div>
      </div>

      <Composer role={role} />
    </div>
  );
}

function Table({ speakers, liveAgent, currentRound }: { speakers: AgentT[]; liveAgent: AgentT | null; currentRound: number }) {
  const W = 720, H = 360;
  const cx = W / 2, cy = H / 2 + 8;
  const rx = 240, ry = 110;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 360 }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <pattern id="g" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M0 0H20M0 0V20" stroke="var(--line-1)" strokeWidth="0.5" />
          </pattern>
          <radialGradient id="spot">
            <stop offset="0%" stopColor="var(--live)" stopOpacity="0.18" />
            <stop offset="60%" stopColor="var(--live)" stopOpacity="0.04" />
            <stop offset="100%" stopColor="var(--live)" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width={W} height={H} fill="url(#g)" opacity="0.4" />
        <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="var(--bg-1)" stroke="var(--line-2)" />
        <ellipse cx={cx} cy={cy} rx={rx - 22} ry={ry - 18} fill="none" stroke="var(--line-1)" strokeDasharray="2 6" />
        <text x={cx} y={cy - 10} fontFamily="var(--mono)" fontSize="10" fill="var(--fg-3)" textAnchor="middle" letterSpacing="2">
          ROUND {currentRound + 1}
        </text>
        <text x={cx} y={cy + 10} fontFamily="var(--mono)" fontSize="13" fill="var(--fg-1)" textAnchor="middle">
          {liveAgent ? `@ ${liveAgent.name.split('·')[0].trim()} has the floor` : 'waiting…'}
        </text>
        {speakers.map((a, i) => {
          const t = (i / speakers.length) * Math.PI * 2 - Math.PI / 2;
          const x = cx + Math.cos(t) * (rx + 50);
          const y = cy + Math.sin(t) * (ry + 30);
          if (!liveAgent || a.short !== liveAgent.short) return null;
          return (
            <g key={a.id}>
              <circle cx={x} cy={y} r="48" fill="url(#spot)" />
              <line x1={x} y1={y} x2={cx} y2={cy} stroke="var(--live)" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="3 3" />
            </g>
          );
        })}
      </svg>

      {speakers.map((a, i) => {
        const t = (i / speakers.length) * Math.PI * 2 - Math.PI / 2;
        const xPct = ((cx + Math.cos(t) * (rx + 50)) / W) * 100;
        const yPct = ((cy + Math.sin(t) * (ry + 30)) / H) * 100;
        const live = !!(liveAgent && a.short === liveAgent.short);
        return <Seat key={a.id} agent={a} xPct={xPct} yPct={yPct} live={live} />;
      })}
    </div>
  );
}

function Seat({ agent, xPct, yPct, live }: { agent: AgentT; xPct: number; yPct: number; live: boolean }) {
  const c = STANCE_COLOR[agent.stance];
  return (
    <div style={{
      position: 'absolute', left: `${xPct}%`, top: `${yPct}%`,
      transform: 'translate(-50%, -50%)',
      width: 132, textAlign: 'center', pointerEvents: 'auto',
    }}>
      <div className="col gap-1 center" style={{ alignItems: 'center' }}>
        <div style={{
          position: 'relative',
          width: 52, height: 52, borderRadius: 6,
          background: c, color: 'var(--bg-0)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 600,
          boxShadow: live ? `0 0 0 2px var(--bg-0), 0 0 0 4px ${c}, 0 0 24px ${c}55` : 'none',
        }}>
          {agent.short}
          {live && (
            <span style={{
              position: 'absolute', bottom: -4, right: -4,
              width: 12, height: 12, borderRadius: '50%',
              background: 'var(--live)', border: '2px solid var(--bg-0)',
            }} />
          )}
        </div>
        <div className="sm" style={{ fontWeight: 500, marginTop: 4, lineHeight: 1.2 }}>
          {agent.name.split('·')[0].trim()}
          <span className="dim mono"> · {agent.name.split('·')[1]?.trim()}</span>
        </div>
        <div className="flex gap-1 center" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
          <StanceTag agent={agent} />
          <ModelChip modelId={agent.model} />
        </div>
      </div>
    </div>
  );
}

function CenterSpeech({ msg, agent }: { msg: ServerMsg; agent: AgentT }) {
  return (
    <div className="col gap-2">
      <div className="flex center gap-2 wrap">
        <Avatar agent={agent} size="sm" />
        <span className="sm" style={{ fontWeight: 500 }}>{agent.name}</span>
        <StanceTag agent={agent} />
        <span className="dim">·</span>
        {agent.model && <ModelChip modelId={agent.model} withCost costCents={msg.cost_cents || 0} />}
        <span className="dim mono tiny" style={{ marginLeft: 'auto' }}>round {msg.round_no + 1} · {fmtTs(msg.created_at)}</span>
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--fg-0)' }}>
        {renderInline(msg.content || '')}{msg._streaming && <span className="caret" />}
      </div>
    </div>
  );
}

function TickerRow({ msg, idx }: { msg: ServerMsg; idx: number }) {
  const a = synthAgent(msg);
  return (
    <div className="hov flex gap-2" style={{ padding: '8px 14px', alignItems: 'flex-start', borderBottom: '1px solid var(--line-1)' }}>
      <span className="mono tiny dim" style={{ width: 22, textAlign: 'right', paddingTop: 2 }}>{String(idx).padStart(2, '0')}</span>
      <Avatar agent={a} size="sm" />
      <div className="col grow" style={{ minWidth: 0 }}>
        <div className="flex center gap-2">
          <span className="sm" style={{ fontWeight: 500 }}>{a.name.split('·')[0].trim()}</span>
          <span className="mono tiny dim">r{msg.round_no + 1}</span>
          <span className="mono tiny dim" style={{ marginLeft: 'auto' }}>{fmtTs(msg.created_at)}</span>
        </div>
        <div className="sm" style={{
          color: 'var(--fg-2)',
          overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          lineHeight: 1.4,
        }}>
          {msg.content}{msg._streaming && <span className="caret" />}
        </div>
      </div>
    </div>
  );
}

function Composer({ role }: { role: Role }) {
  const { t } = useI18n();
  return (
    <div className="col room-composer" style={{ padding: 12, gap: 8, borderTop: '1px solid var(--line-1)', background: 'var(--bg-1)' }}>
      <div className="flex gap-2">
        <textarea className="input mono" rows={1}
          placeholder={role === 'director' ? '$ director › human injection — coming next wave' : '$ speak as ' + role + ' — coming next wave'}
          disabled />
        <button className="btn primary" style={{ padding: '0 16px' }} disabled
          title="Human injection — coming next wave">{I.send} {t('app.room.send')}</button>
        {role === 'director' && (
          <>
            <button className="btn" disabled title="pause from sidebar">{I.pause}</button>
            <button className="btn" disabled title="fork — coming next wave">{I.fork} {t('app.room.director.fork')}</button>
          </>
        )}
      </div>
    </div>
  );
}
